import { useVirtualizer } from "@tanstack/react-virtual";
import { useDeleteGifMutation } from "../api/mutations";
import { useCanManageGallery } from "../api/queries";
import GallerySpinner from "./GallerySpinner";
import GalleryLoader from "./GalleryLoader";
import { TrashIcon, PlayIcon } from "@heroicons/react/24/solid";
import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import { useState, useCallback, useEffect, useRef, memo } from "react";
import { toast } from "sonner";
import { t } from "copy/t";
import useContainerDimensions from "../hooks/useContainerDimensions";

const MIN_ITEM_WIDTH_MOBILE = 120;
const MIN_ITEM_WIDTH_DESKTOP = 150;
const OVERSCAN = 5;
const GAP = 2;
const LOAD_MORE_THRESHOLD = 1500;
// Under this many photos the grid is a "few photos" layout: one column per
// photo (up to what the width allows), tiles up to FEW_MAX_CELL wide, centred
// under the hero, so a one-photo gallery shows one large tile rather than a
// small one pinned to the left edge.
export const FEW_PHOTOS = 8;
const FEW_MAX_CELL = 400;

/**
 * Columns, grid width and tile width for `count` photos in `containerWidth`.
 * Many photos fill the width edge to edge; a few are capped and centred.
 */
export function gridLayout(containerWidth, count) {
  if (!(containerWidth > 0)) return { columnCount: 1, gridWidth: 0, cellWidth: 0 };
  const minItemWidth = containerWidth < 640 ? MIN_ITEM_WIDTH_MOBILE : MIN_ITEM_WIDTH_DESKTOP;
  const widthColumns = Math.max(1, Math.floor(containerWidth / minItemWidth));
  const few = count > 0 && count < FEW_PHOTOS;
  const columnCount = few ? Math.min(widthColumns, count) : widthColumns;
  const gaps = (columnCount - 1) * GAP;
  const gridWidth = few
    ? Math.min(containerWidth, columnCount * FEW_MAX_CELL + gaps)
    : containerWidth;
  return { columnCount, gridWidth, cellWidth: (gridWidth - gaps) / columnCount };
}

function GalleryList({
  slug,
  gifs,
  error,
  onImageClick,
  loading,
  setGifs,
  onLoadMore,
  hasMore,
  emptyTextColor,
}) {
  const [open, setOpen] = useState(false);
  const [gifToDelete, setGifToDelete] = useState(null);
  const [gifImageUrl, setGifImageUrl] = useState(null);
  // Server-authoritative: owner, channel admin or super_admin (GifPolicy#destroy?).
  const canDelete = useCanManageGallery(slug);
  const deleteGifMutation = useDeleteGifMutation();
  const loadMoreCalledRef = useRef(false);
  const prevRowCountRef = useRef(0);

  const { width: containerWidth, ref: containerRef } = useContainerDimensions();

  const errorMessage = error?.message;
  const useBigImages = gifs?.length < FEW_PHOTOS;

  const { columnCount, gridWidth, cellWidth } = gridLayout(containerWidth, gifs?.length || 0);
  const rowHeight = Math.round(cellWidth * (4 / 3));
  const rowCount = gifs?.length ? Math.ceil(gifs.length / columnCount) : 0;

  const getScrollElement = useCallback(
    () => document.getElementById("scrollableDiv"),
    []
  );

  const estimateRowSize = useCallback(() => rowHeight, [rowHeight]);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement,
    estimateSize: estimateRowSize,
    overscan: OVERSCAN,
    gap: GAP,
  });

  // Scroll-based load-more detection
  useEffect(() => {
    const el = document.getElementById("scrollableDiv");
    if (!el) return;
    loadMoreCalledRef.current = false;
    const check = () => {
      if (!hasMore || loading) return;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD;
      if (nearBottom && !loadMoreCalledRef.current) {
        loadMoreCalledRef.current = true;
        onLoadMore();
      } else if (!nearBottom) {
        loadMoreCalledRef.current = false;
      }
    };
    el.addEventListener("scroll", check, { passive: true });
    // Only proactively check when new rows were added, not when loading finished
    if (rowCount > prevRowCountRef.current) {
      check();
    }
    prevRowCountRef.current = rowCount;
    return () => el.removeEventListener("scroll", check);
  }, [hasMore, loading, onLoadMore, rowCount]);

  const handleOpenSingleView = useCallback((index) => {
    onImageClick(index);
  }, [onImageClick]);

  const handleDelete = useCallback(() => {
    if (gifToDelete) {
      deleteGifMutation.mutate(gifToDelete, {
        onSuccess: () => {
          setGifs((prev) => prev.filter((gif) => gif.id !== gifToDelete));
          toast.success(t("toast.gallery.gifDeleted"));
          setOpen(false);
          setGifToDelete(null);
          setGifImageUrl(null);
        },
        onError: () => {
          toast.error(t("toast.gallery.gifDeleteFailed"));
        },
      });
    }
  }, [gifToDelete, deleteGifMutation, setGifs]);

  const handleDeleteClick = useCallback((e, gif) => {
    e.stopPropagation();
    setGifToDelete(gif.id);
    setGifImageUrl(gif.image_urls?.big || gif.grid_image_url || gif.image_url);
    setOpen(true);
  }, []);

  if (!gifs?.length) {
    if (loading) return <GalleryLoader />;
    return (
      // `emptyTextColor` follows the gallery colour, so the message stays
      // legible on a light gallery; white at half strength is the fallback.
      <p
        className={`mx-auto px-4 text-xl font-inter font-normal max-w-md text-center text-balance ${
          emptyTextColor ? "" : "text-white/50"
        }`}
        style={emptyTextColor ? { color: emptyTextColor } : undefined}
      >
        {errorMessage || "No sessions to display for this timeframe."}
      </p>
    );
  }

  return (
    <div>
      <ConfirmModal
        open={open}
        setOpen={setOpen}
        onConfirm={handleDelete}
        onCancel={() => {
          setOpen(false);
          setGifToDelete(null);
          setGifImageUrl(null);
        }}
        title="Delete Image?"
        // Counsel-verified copy. The Leads sentence is unconditional today: no AI Headshot session can be
        // created in production (rails AI_CAPTURE_CONSENT_GATE_SHIPPED=false). BEFORE that flips or
        // ai_capture_mode_enabled turns on, show it only for non-held photos via a rails per-gif field.
        // See counsel opinion 2026-10-06-mb-help-deletion-wording, Addendum 2 (3a2385c).
        message="You can't undo this. This also deletes the Leads entries of guests who received this photo."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        imageUrl={gifImageUrl}
        isLoading={deleteGifMutation.isPending}
      />

      <div ref={containerRef} className="w-full">
        {containerWidth > 0 && (
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: gridWidth,
              maxWidth: "100%",
              margin: "0 auto",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => (
              <div
                key={virtualRow.key}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                    gap: `${GAP}px`,
                  }}
                >
                  {Array.from({ length: columnCount }).map((_, colIndex) => {
                    const gifIndex = virtualRow.index * columnCount + colIndex;
                    if (gifIndex >= gifs.length) {
                      return <div key={colIndex} />;
                    }
                    const gif = gifs[gifIndex];
                    return (
                      // Photo tile: keyboard-operable via role/tabIndex/onKeyDown
                      // rather than a native <button> because it wraps the nested
                      // delete <button> (button-in-button is invalid).
                      <div
                        key={gif.id}
                        // Visible keyboard focus indicator (focus-visible:outline)
                        // is master's #259 a11y win; the unique position-bearing
                        // name ("Open image, N of M") is staging's — both kept.
                        className="relative cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                        role="button"
                        tabIndex={0}
                        aria-label={`${gif.is_video && gif.video_url ? "Play video" : "Open image"}, ${gifIndex + 1} of ${gifs.length}`}
                        onClick={() => handleOpenSingleView(gifIndex)}
                        onKeyDown={(e) => {
                          // Only act when the tile ITSELF is the key target.
                          // The nested delete <button> natively handles
                          // Enter/Space and the event bubbles up to here, so
                          // without this guard pressing a key on the trash
                          // icon would also open the single view.
                          if (e.target !== e.currentTarget) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleOpenSingleView(gifIndex);
                          }
                        }}
                      >
                        <img
                          alt="Gallery image"

                          src={
                            useBigImages
                              ? gif.image_urls?.big || gif.grid_image_url
                              : gif.grid_image_url || gif.image_url
                          }
                          className="object-cover w-full aspect-[3/4]"
                        />
                        {gif.is_video && gif.video_url ? (
                          // Play badge only: the clip itself plays in the
                          // single view, never autoplaying across the grid.
                          <span
                            data-testid="video-play-badge"
                            aria-hidden="true"
                            className="absolute right-1 bottom-1 w-8 h-8 flex justify-center items-center rounded-full pointer-events-none"
                            style={{
                              background: "rgba(0, 0, 0, 0.4)",
                              backdropFilter: "blur(8px)",
                            }}
                          >
                            <PlayIcon className="text-white w-4" />
                          </span>
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            aria-label={`Delete image, ${gifIndex + 1} of ${gifs.length}`}
                            className="absolute left-1 top-1 w-8 h-8 flex justify-center items-center rounded-full disabled:opacity-50 opacity-100 z-10 hover:!bg-red-600 transition-colors"
                            style={{
                              background: "rgba(0, 0, 0, 0.16)",
                              backdropFilter: "blur(8px)",
                            }}
                            onClick={(e) => handleDeleteClick(e, gif)}
                            disabled={deleteGifMutation.isPending}
                          >
                            <TrashIcon className="text-white w-4" aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {deleteGifMutation.isPending && <GallerySpinner />}
    </div>
  );
}

export default memo(GalleryList);
